import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  AreaChart, Area
} from 'recharts';
import { Terminal, Hash, Zap, User } from 'lucide-react';
import Sidebar from '@/components/Sidebar';
import { useGuild } from '@/contexts/GuildContext';
import { Skeleton } from '@/components/ui/skeleton';

const API = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#5865F2', '#D946EF', '#10B981', '#F59E0B', '#EF4444', '#3B82F6', '#8B5CF6', '#06B6D4', '#EC4899', '#14B8A6'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border-md)] rounded-lg px-3 py-2 text-xs">
      <p className="text-zinc-400 mb-1">{label}</p>
      {payload.map(p => (
        <p key={p.dataKey} style={{ color: p.color }}>{p.name}: {p.value}</p>
      ))}
    </div>
  );
};

function StatCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="stat-card">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest mb-2 font-[Manrope]">{label}</p>
          <p className="text-4xl font-mono-stats font-bold text-white">{value ?? '—'}</p>
          {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
        </div>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: accent + '22' }}>
          <Icon size={20} style={{ color: accent }} />
        </div>
      </div>
    </motion.div>
  );
}

export default function BotUsage() {
  const { selectedGuildId } = useGuild();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedGuildId) return;
    setLoading(true);
    axios.get(`${API}/api/commands/usage`, { params: { guild_id: selectedGuildId } })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedGuildId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)]">
        <Sidebar />
        <main className="lg:ml-60 pt-16 lg:pt-0 p-6">
          <div className="mb-8">
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-4 w-72" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="stat-card">
                <Skeleton className="h-3 w-20 mb-3" />
                <Skeleton className="h-9 w-16 mb-2" />
                <Skeleton className="h-3 w-14" />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-6">
            <Skeleton className="lg:col-span-3 h-64 rounded-xl" />
            <Skeleton className="lg:col-span-2 h-64 rounded-xl" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Skeleton className="h-48 rounded-xl" />
            <Skeleton className="h-48 rounded-xl" />
          </div>
        </main>
      </div>
    );
  }

  const topCommand = (data?.by_command || [])[0];

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Sidebar />
      <main className="lg:ml-60 pt-16 lg:pt-0 p-6" data-testid="bot-usage-main">
        <div className="mb-8">
          <h1 className="text-3xl font-bold font-[Outfit] text-white">Bot Usage</h1>
          <p className="text-zinc-500 text-sm mt-1 font-[Manrope]">Command analytics and activity patterns</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard icon={Terminal} label="Total Commands" value={(data?.total || 0).toLocaleString()} sub="All time" accent="#5865F2" />
          <StatCard icon={Hash} label="Unique Commands" value={(data?.by_command || []).length} sub="Different types" accent="#10B981" />
          <StatCard icon={Zap} label="Most Used" value={topCommand?.command ? `/${topCommand.command}` : '—'} sub={topCommand ? `${topCommand.count} uses` : ''} accent="#D946EF" />
          <StatCard icon={User} label="Top User" value={(data?.top_users || [])[0]?.username?.split('#')[0] || '—'} sub={(data?.top_users || [])[0] ? `${data.top_users[0].count} commands` : ''} accent="#F59E0B" />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-6">
          {/* Command by type */}
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="lg:col-span-3 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5"
            data-testid="commands-by-type-chart"
          >
            <h3 className="text-sm font-semibold text-white mb-1 font-[Outfit]">Commands by Type</h3>
            <p className="text-xs text-zinc-500 mb-4 font-[Manrope]">Usage distribution across all commands</p>
            {(data?.by_command || []).length === 0 ? (
              <p className="text-zinc-500 text-sm text-center py-12">No commands used yet</p>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={(data?.by_command || []).slice(0, 10)}>
                  <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="command" tick={{ fill: '#71717A', fontSize: 10 }} tickLine={false} axisLine={false}
                    tickFormatter={v => `/${v}`} />
                  <YAxis tick={{ fill: '#71717A', fontSize: 10 }} tickLine={false} axisLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Uses" fill="#5865F2" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </motion.div>

          {/* Top Users */}
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="lg:col-span-2 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5"
            data-testid="top-users-list"
          >
            <h3 className="text-sm font-semibold text-white mb-1 font-[Outfit]">Top Users</h3>
            <p className="text-xs text-zinc-500 mb-4 font-[Manrope]">Most active bot users</p>
            {(data?.top_users || []).length === 0 ? (
              <p className="text-zinc-500 text-sm text-center py-8">No data yet</p>
            ) : (
              <div className="space-y-3">
                {(data?.top_users || []).map((u, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                      style={{ background: COLORS[i % COLORS.length] }}>
                      {i + 1}
                    </div>
                    {u.avatar ? (
                      <img src={u.avatar} alt={u.username} className="w-8 h-8 rounded-full shrink-0 border border-[var(--border-md)]" data-testid={`top-user-avatar-${i}`} />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-[var(--bg-hover)] border border-[var(--border-md)] flex items-center justify-center text-xs font-semibold text-zinc-400 shrink-0 font-[Outfit]">
                        {u.username?.[0]?.toUpperCase() || '?'}
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="text-sm text-white font-[Manrope] truncate">{u.username}</p>
                      <div className="w-full h-1.5 bg-[var(--bg-hover)] rounded-full mt-1">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${(u.count / ((data?.top_users || [])[0]?.count || 1)) * 100}%`,
                            background: COLORS[i % COLORS.length]
                          }}
                        />
                      </div>
                    </div>
                    <span className="text-zinc-400 text-xs font-mono-stats">{u.count}</span>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>

        {/* Hourly Heatmap */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5"
            data-testid="hourly-heatmap"
          >
            <h3 className="text-sm font-semibold text-white mb-1 font-[Outfit]">Activity Heatmap</h3>
            <p className="text-xs text-zinc-500 mb-4 font-[Manrope]">Bot usage by hour of day</p>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={data?.heatmap || []}>
                <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="hour" tick={{ fill: '#71717A', fontSize: 9 }} tickLine={false} axisLine={false} interval={3} />
                <YAxis tick={{ fill: '#71717A', fontSize: 9 }} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" name="Commands" fill="#10B981" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
            className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5"
            data-testid="daily-commands-chart"
          >
            <h3 className="text-sm font-semibold text-white mb-1 font-[Outfit]">Daily Commands</h3>
            <p className="text-xs text-zinc-500 mb-4 font-[Manrope]">Last 14 days</p>
            <ResponsiveContainer width="100%" height={160}>
              <AreaChart data={data?.daily_chart || []}>
                <defs>
                  <linearGradient id="cmdGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="date" tick={{ fill: '#71717A', fontSize: 9 }} tickLine={false} axisLine={false} interval={2} />
                <YAxis tick={{ fill: '#71717A', fontSize: 9 }} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="commands" name="Commands" stroke="#10B981" fill="url(#cmdGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
